import { Box, Typography, Grid, TableContainer, Table, TableRow, TableBody, Container, Divider, TableHead } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import ConditionalParts from './conditionalParts';
import { ConditionalTableBodyCell, ConditionalTableHeadCell, GreenButton, ItemLabelBox, ItemLabelTypography, WhiteButton } from './inputSearchCondition.style';
import { conditionItemList, initialSearchItemsState } from './const';
import SwalUtil from "../../shared/util/sweetalert/sweetalertUtil";
import SaveCondition from '../estimateCondition/saveCondition';
import LoadCondition from '../estimateCondition/loadCondition';
import { sessionDisconnectedCheck } from '../../shared/api/sessionDisconnectedCheck';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { SystemErrorMsg, SystemErrorTitle } from '../../constants';
import { BackdropLoading } from '../../shared/spinner/backdropLoading';
import { GetKigyoCountApiRequest, GetSearchResultApiRequest } from '../../shared/model/apiRequestModel';
import getKigyoCount from '../../shared/api/getKigyoCount';
import getSearchResult from '../../shared/api/getSearchResult';
import activateSession from '../../shared/api/activateSession';

interface ResultSearchCondition {
    operator: string
    itemLabel: string
    count: number
    value: string
}

interface LoadSearchCondition {
    operator: string
    itemCode: string
    itemName: string
    inputValue: string
    inputLabel?: string
    matchFlag?: boolean
}

interface SaveParameter {
    itemCode: string
    itemName: string
    operator: string
    inputValue: string
    inputLabel?: string
    matchFlag?: boolean
}

interface InputSearchConditionProps {
    conditions: SaveParameter[];
}

interface SearchItem {
    item: string;
    itemLabel: string;
    value: string;
    valueLabel: string;
    operator: 'AND' | 'OR';
    match: string;
}

interface SearchItemState {
    itemCode: string;
    operator: 'AND' | 'OR';
    inputValue: string;
    inputLabel?: string;
    matchFlag?: boolean;
}

interface HandleFunction {
    chengeOperatorFunction: (selectVal: 'AND' | 'OR') => void
    chengeValFunction: (inputVal: string, inputLabel?: string) => void
    chengeMatchCheckboxFunction?: (isChecked: boolean) => void
    clearValFunction?: () => void
}

const defaultSearchConditions: SaveParameter[] = []

const screenId = "U040"

const InputSearchCondition: FC<InputSearchConditionProps> = ({ conditions = defaultSearchConditions }) => {
    const navigate = useNavigate();
    const location = useLocation() as { state?: { fromResult?: ResultSearchCondition[], searchItemsState?: { [key: string]: SearchItemState } } };

    const [searchItemsState, setSearchItemsState] = useState<{ [key: string]: SearchItemState }>(location.state?.searchItemsState ?? initialSearchItemsState);
    const kigyoCodeHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, kigyoCode: { ...prev.kigyoCode, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string) => {
            if (inputVal.length <= 9) {
                setSearchItemsState(prev => ({ ...prev, kigyoCode: { ...prev.kigyoCode, inputValue: inputVal.replaceAll(/[^0-9]/g, "") } }));
            }
        }
    }), [])
    const jojoHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, jojo: { ...prev.jojo, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string, inputLabel?: string) => {
            setSearchItemsState(prev => ({ ...prev, jojo: { ...prev.jojo, inputValue: inputVal, inputLabel: inputLabel } }));
        },
        clearValFunction: () => {
            setSearchItemsState(prev => ({ ...prev, jojo: { ...prev.jojo, inputValue: '', inputLabel: '' } }));
        }
    }), [])
    const shogoKanaHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, shogoKana: { ...prev.shogoKana, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string) => {
            if (inputVal.length <= 60) {
                setSearchItemsState(prev => ({ ...prev, shogoKana: { ...prev.shogoKana, inputValue: inputVal } }));
            }
        },
        chengeMatchCheckboxFunction: (isChecked: boolean) => {
            setSearchItemsState(prev => ({ ...prev, shogoKana: { ...prev.shogoKana, matchFlag: isChecked } }));
        }
    }), [])
    const shogoKanjiHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, shogoKanji: { ...prev.shogoKanji, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string) => {
            if (inputVal.length <= 100) {
                setSearchItemsState(prev => ({ ...prev, shogoKanji: { ...prev.shogoKanji, inputValue: inputVal } }));
            }
        },
        chengeMatchCheckboxFunction: (isChecked: boolean) => {
            setSearchItemsState(prev => ({ ...prev, shogoKanji: { ...prev.shogoKanji, matchFlag: isChecked } }));
        }
    }), [])
    const daihyoshaKanaHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, daihyoshaKana: { ...prev.daihyoshaKana, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string) => {
            if (inputVal.length <= 200) {
                setSearchItemsState(prev => ({ ...prev, daihyoshaKana: { ...prev.daihyoshaKana, inputValue: inputVal } }));
            }
        },
        chengeMatchCheckboxFunction: (isChecked: boolean) => {
            setSearchItemsState(prev => ({ ...prev, daihyoshaKana: { ...prev.daihyoshaKana, matchFlag: isChecked } }));
        }
    }), [])
    const daihyoshaKanjiHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, daihyoshaKanji: { ...prev.daihyoshaKanji, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string) => {
            if (inputVal.length <= 200) {
                setSearchItemsState(prev => ({ ...prev, daihyoshaKanji: { ...prev.daihyoshaKanji, inputValue: inputVal } }));
            }
        },
        chengeMatchCheckboxFunction: (isChecked: boolean) => {
            setSearchItemsState(prev => ({ ...prev, daihyoshaKanji: { ...prev.daihyoshaKanji, matchFlag: isChecked } }));
        }
    }), [])
    const yubinNoHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, yubinNo: { ...prev.yubinNo, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string) => {
            if (inputVal.length <= 8) {
                setSearchItemsState(prev => ({ ...prev, yubinNo: { ...prev.yubinNo, inputValue: inputVal.replaceAll(/[^0-9]/g, "") } }));
            }
        }
    }), [])
    const shozaichiHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, shozaichi: { ...prev.shozaichi, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string, inputLabel?: string) => {
            setSearchItemsState(prev => ({ ...prev, shozaichi: { ...prev.shozaichi, inputValue: inputVal, inputLabel: inputLabel } }));
        },
        clearValFunction: () => {
            setSearchItemsState(prev => ({ ...prev, shozaichi: { ...prev.shozaichi, inputValue: '', inputLabel: '' } }));
        }
    }), [])
    const denwaNoHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, denwaNo: { ...prev.denwaNo, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string) => {
            if (inputVal.length <= 21) {
                setSearchItemsState(prev => ({ ...prev, denwaNo: { ...prev.denwaNo, inputValue: inputVal.replaceAll(/[^0-9]/g, "") } }));
            }
        }
    }), [])
    const setsuritsuHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, setsuritsu: { ...prev.setsuritsu, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string, inputLabel?: string) => {
            setSearchItemsState(prev => ({ ...prev, setsuritsu: { ...prev.setsuritsu, inputValue: inputVal, inputLabel: inputLabel } }));
        },
        clearValFunction: () => {
            setSearchItemsState(prev => ({ ...prev, setsuritsu: { ...prev.setsuritsu, inputValue: '', inputLabel: '' } }));
        }
    }), [])
    const sogyoHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, sogyo: { ...prev.sogyo, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string, inputLabel?: string) => {
            setSearchItemsState(prev => ({ ...prev, sogyo: { ...prev.sogyo, inputValue: inputVal, inputLabel: inputLabel } }));
        },
        clearValFunction: () => {
            setSearchItemsState(prev => ({ ...prev, sogyo: { ...prev.sogyo, inputValue: '', inputLabel: '' } }));
        }
    }), [])
    const shihonkinHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, shihonkin: { ...prev.shihonkin, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string, inputLabel?: string) => {
            setSearchItemsState(prev => ({ ...prev, shihonkin: { ...prev.shihonkin, inputValue: inputVal, inputLabel: inputLabel } }));
        },
        clearValFunction: () => {
            setSearchItemsState(prev => ({ ...prev, shihonkin: { ...prev.shihonkin, inputValue: '', inputLabel: '' } }));
        }
    }), [])
    const jugyoinsuHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, jugyoinsu: { ...prev.jugyoinsu, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string, inputLabel?: string) => {
            setSearchItemsState(prev => ({ ...prev, jugyoinsu: { ...prev.jugyoinsu, inputValue: inputVal, inputLabel: inputLabel } }));
        },
        clearValFunction: () => {
            setSearchItemsState(prev => ({ ...prev, jugyoinsu: { ...prev.jugyoinsu, inputValue: '', inputLabel: '' } }));
        }
    }), [])
    const gyoshuHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, gyoshu: { ...prev.gyoshu, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string, inputLabel?: string) => {
            setSearchItemsState(prev => ({ ...prev, gyoshu: { ...prev.gyoshu, inputValue: inputVal, inputLabel: inputLabel } }));
        },
        clearValFunction: () => {
            setSearchItemsState(prev => ({ ...prev, gyoshu: { ...prev.gyoshu, inputValue: '', inputLabel: '' } }));
        }
    }), [])
    const yakuinHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, yakuin: { ...prev.yakuin, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string) => {
            if (inputVal.length <= 33) {
                setSearchItemsState(prev => ({ ...prev, yakuin: { ...prev.yakuin, inputValue: inputVal } }));
            }
        }
    }), [])
    const okabunushiHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, okabunushi: { ...prev.okabunushi, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string) => {
            if (inputVal.length <= 100) {
                setSearchItemsState(prev => ({ ...prev, okabunushi: { ...prev.okabunushi, inputValue: inputVal } }));
            }
        }
    }), [])
    const eigyoshumokuHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, eigyoshumoku: { ...prev.eigyoshumoku, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string) => {
            if (inputVal.length <= 30) {
                setSearchItemsState(prev => ({ ...prev, eigyoshumoku: { ...prev.eigyoshumoku, inputValue: inputVal } }));
            }
        }
    }), [])
    const shiiresakiHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, shiiresaki: { ...prev.shiiresaki, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string) => {
            if (inputVal.length <= 60) {
                setSearchItemsState(prev => ({ ...prev, shiiresaki: { ...prev.shiiresaki, inputValue: inputVal } }));
            }
        }
    }), [])
    const hanbaisakiHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, hanbaisaki: { ...prev.hanbaisaki, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string) => {
            if (inputVal.length <= 60) {
                setSearchItemsState(prev => ({ ...prev, hanbaisaki: { ...prev.hanbaisaki, inputValue: inputVal } }));
            }
        }
    }), [])
    const torihikiginkoHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, torihikiginko: { ...prev.torihikiginko, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string, inputLabel?: string) => {
            setSearchItemsState(prev => ({ ...prev, torihikiginko: { ...prev.torihikiginko, inputValue: inputVal, inputLabel: inputLabel } }));
        },
        clearValFunction: () => {
            setSearchItemsState(prev => ({ ...prev, torihikiginko: { ...prev.torihikiginko, inputValue: '', inputLabel: '' } }));
        }
    }), [])
    const kessangetsuHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, kessangetsu: { ...prev.kessangetsu, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string, inputLabel?: string) => {
            setSearchItemsState(prev => ({ ...prev, kessangetsu: { ...prev.kessangetsu, inputValue: inputVal, inputLabel: inputLabel } }));
        },
        clearValFunction: () => {
            setSearchItemsState(prev => ({ ...prev, kessangetsu: { ...prev.kessangetsu, inputValue: '', inputLabel: '' } }));
        }
    }), [])
    const saishinuriageHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, saishinuriage: { ...prev.saishinuriage, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string, inputLabel?: string) => {
            setSearchItemsState(prev => ({ ...prev, saishinuriage: { ...prev.saishinuriage, inputValue: inputVal, inputLabel: inputLabel } }));
        },
        clearValFunction: () => {
            setSearchItemsState(prev => ({ ...prev, saishinuriage: { ...prev.saishinuriage, inputValue: '', inputLabel: '' } }));
        }
    }), [])
    const saishinriekiHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, saishinrieki: { ...prev.saishinrieki, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string, inputLabel?: string) => {
            setSearchItemsState(prev => ({ ...prev, saishinrieki: { ...prev.saishinrieki, inputValue: inputVal, inputLabel: inputLabel } }));
        },
        clearValFunction: () => {
            setSearchItemsState(prev => ({ ...prev, saishinrieki: { ...prev.saishinrieki, inputValue: '', inputLabel: '' } }));
        }
    }), [])
    const uriageshinchoritsuHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, uriageshinchoritsu: { ...prev.uriageshinchoritsu, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string, inputLabel?: string) => {
            setSearchItemsState(prev => ({ ...prev, uriageshinchoritsu: { ...prev.uriageshinchoritsu, inputValue: inputVal, inputLabel: inputLabel } }));
        },
        clearValFunction: () => {
            setSearchItemsState(prev => ({ ...prev, uriageshinchoritsu: { ...prev.uriageshinchoritsu, inputValue: '', inputLabel: '' } }));
        }
    }), [])
    const riekishinchoritsuHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, riekishinchoritsu: { ...prev.riekishinchoritsu, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string, inputLabel?: string) => {
            setSearchItemsState(prev => ({ ...prev, riekishinchoritsu: { ...prev.riekishinchoritsu, inputValue: inputVal, inputLabel: inputLabel } }));
        },
        clearValFunction: () => {
            setSearchItemsState(prev => ({ ...prev, riekishinchoritsu: { ...prev.riekishinchoritsu, inputValue: '', inputLabel: '' } }));
        }
    }), [])
    const seinengappiHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, seinengappi: { ...prev.seinengappi, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string, inputLabel?: string) => {
            setSearchItemsState(prev => ({ ...prev, seinengappi: { ...prev.seinengappi, inputValue: inputVal, inputLabel: inputLabel } }));
        },
        clearValFunction: () => {
            setSearchItemsState(prev => ({ ...prev, seinengappi: { ...prev.seinengappi, inputValue: '', inputLabel: '' } }));
        }
    }), [])
    const etoHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, eto: { ...prev.eto, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string, inputLabel?: string) => {
            setSearchItemsState(prev => ({ ...prev, eto: { ...prev.eto, inputValue: inputVal, inputLabel: inputLabel } }));
        },
        clearValFunction: () => {
            setSearchItemsState(prev => ({ ...prev, eto: { ...prev.eto, inputValue: '', inputLabel: '' } }));
        }
    }), [])
    const shusshinkoHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, shusshinko: { ...prev.shusshinko, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string, inputLabel?: string) => {
            setSearchItemsState(prev => ({ ...prev, shusshinko: { ...prev.shusshinko, inputValue: inputVal } }));
        },
        clearValFunction: () => {
            setSearchItemsState(prev => ({ ...prev, shusshinko: { ...prev.shusshinko, inputValue: '', inputLabel: '' } }));
        }
    }), [])
    const shusshinchiHandleFunction: HandleFunction = useMemo(() => ({
        chengeOperatorFunction: (selectVal: 'AND' | 'OR') => {
            setSearchItemsState(prev => ({ ...prev, shusshinchi: { ...prev.shusshinchi, operator: selectVal } }));
        },
        chengeValFunction: (inputVal: string, inputLabel?: string) => {
            setSearchItemsState(prev => ({ ...prev, shusshinchi: { ...prev.shusshinchi, inputValue: inputVal, inputLabel: inputLabel } }));
        },
        clearValFunction: () => {
            setSearchItemsState(prev => ({ ...prev, shusshinchi: { ...prev.shusshinchi, inputValue: '', inputLabel: '' } }));
        }
    }), [])

    useEffect(() => {
        const interval = setInterval(async () => {
            // セッションの有効性確認
            if (await sessionDisconnectedCheck(screenId, "polling", navigate)) {
                return;
            }
        }, 30000);
        return () => clearInterval(interval);
    }, []);

    // 件数取得処理済を監視
    const [isSearched, setIsSearched] = useState(false);
    useEffect(() => {
        setIsSearched(true);
    }, [])

    // 初期表示時の処理
    const [personalInfoProtectFlag, setPersonalInfoProtectFlag] = useState(false);
    const [loading, setLoading] = useState(false);

    const init = useCallback(() => {
        if (conditions.length !== 0) {
            const newSearchItemState = JSON.parse(JSON.stringify(searchItemsState));
            for (let condition of conditions) {
                const { itemCode, inputValue, inputLabel = '', operator, matchFlag = true } = condition;
                newSearchItemState[itemCode].operator = operator;
                newSearchItemState[itemCode].inputValue = inputValue;
                if (newSearchItemState[itemCode].inputLabel !== undefined) {
                    newSearchItemState[itemCode].inputLabel = inputLabel;
                }
                if (newSearchItemState[itemCode].matchFlag !== undefined) {
                    newSearchItemState[itemCode].matchFlag = matchFlag;
                }
            }
            setSearchItemsState(newSearchItemState);
        }
    }, [conditions, searchItemsState]);

    const multipleLoginCheck = async () => {
        const session = await fetchAuthSession();
        let setCncurrentFlg = false;
        // 多重ログイン確認用フラグ
        const concurrentFlg = session?.tokens?.idToken?.payload?.concurrentFlg;
        const isLoginChecked = sessionStorage.getItem("isLoginChecked");
        if (concurrentFlg == "1") {
            setCncurrentFlg = true;
        }
        if (setCncurrentFlg && !isLoginChecked) {
            SwalUtil.labelInfo({
                title: "多重ログイン",
                message: '他のユーザーがログイン中です。<br/>操作を続けますか？',
                textAlign: "left",
                confirmButtonText: "OK",
                denyButtonText: "キャンセル",
                buttonAlign: "right",
                buttonOrder: { confirm: 2, deny: 1 }
            }).then(async (result) => {
                if (result.isConfirmed) {
                    setLoading(true)
                    const response = activateSession();
                    if (await response === "E9999") {
                        navigate("/Error", { state: { title: SystemErrorTitle, message: SystemErrorMsg } });
                    }
                    init();
                    sessionStorage.setItem("isLoginChecked", "true");
                    setLoading(false)
                } else if (result.isDenied) {
                    signOut();
                }
                setCncurrentFlg = false
            });
        } else {
            init();
        }
    }

    // 個人情報保護フラグ取得
    const getPersonalInfo = async () => {
        const session = await fetchAuthSession();
        const contract = JSON.parse(session?.tokens?.idToken?.payload.contractInfoData?.toString() || "[]");
        const personalInfo = contract.length !== 0 && contract[0].personalInfo;
        setPersonalInfoProtectFlag(personalInfo);
    }

    useEffect(() => {
        // 多重ログインチェック
        multipleLoginCheck();
        // 個人情報保護フラグ取得
        getPersonalInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 検索条件の保存ダイアログ表示
    const [saveParameterJson, setSaveParameterJson] = useState('');
    const [saveOpen, setSaveOpen] = useState(false);

    const createSaveParameter = (itemCode: string) => {
        const saveParameter = {
            'itemCode': itemCode,
            'itemName': conditionItemList[itemCode].itemName,
            'operator': searchItemsState[itemCode].operator,
            'inputValue': searchItemsState[itemCode].inputValue,
            'inputLabel': searchItemsState[itemCode].inputLabel ?? '',
            'matchFlag': searchItemsState[itemCode].matchFlag,
        }
        return saveParameter;
    }
    const handleClickSave = async () => {
        // セッションの有効性確認
        if (await sessionDisconnectedCheck(screenId, 'handleClickSave', navigate)) {
            return
        }
        const itemCodeList = Object.keys(conditionItemList);
        const saveParameters: SaveParameter[] = []
        for (let itemCode of itemCodeList) {
            if (searchItemsState[itemCode].inputValue !== '') {
                saveParameters.push(createSaveParameter(itemCode));
            }
        }
        setSaveParameterJson(JSON.stringify(saveParameters));
        setSaveOpen(true);
    }

    // 検索条件の読込・削除ダイアログ表示
    const [loadAndDeleteOpen, setLoadAndDeleteOpen] = useState(false);
    const handleClickLoadAndDelete = async () => {
        // セッションの有効性確認
        if (await sessionDisconnectedCheck(screenId, 'handleClickLoadAndDelete', navigate)) {
            return
        }
        setLoadAndDeleteOpen(true);
    }

    const handleLoad = (searchConditions: LoadSearchCondition[]) => {
        const newSearchItemState = JSON.parse(JSON.stringify(initialSearchItemsState));
        for (let condition of searchConditions) {
            const { itemCode, inputValue, inputLabel = '', operator, matchFlag = true } = condition;
            newSearchItemState[itemCode].operator = operator;
            newSearchItemState[itemCode].inputValue = inputValue;
            if (newSearchItemState[itemCode].inputLabel !== undefined) {
                newSearchItemState[itemCode].inputLabel = inputLabel;
            }
            if (newSearchItemState[itemCode].matchFlag !== undefined) {
                newSearchItemState[itemCode].matchFlag = matchFlag;
            }
        }
        setSearchItemsState(newSearchItemState);
        setLoadAndDeleteOpen(false);
    }

    // 企業件数取得API呼び出し
    const [searchRequest, setSearchRequest] = useState<GetKigyoCountApiRequest>();
    // 検索結果画面から戻る場合は条件を残す
    const [resultConditionList, setResultConditionList] = useState<ResultSearchCondition[]>(location.state?.fromResult ?? []);
    const [resultCount, setResultCount] = useState(0);

    const createSearchItemRequest = (itemCode: string) => {
        const searchItem = {
            'item': itemCode,
            'itemLabel': conditionItemList[itemCode].itemName,
            'operator': searchItemsState[itemCode].operator,
            'value': searchItemsState[itemCode].inputValue,
            'valueLabel': searchItemsState[itemCode].inputLabel ?? '',
            'match': '',
        }
        if (conditionItemList[itemCode].matchCheckboxFlag === true) {
            searchItem.match = searchItemsState[itemCode].matchFlag ? conditionItemList[itemCode].matchPattern[0] : conditionItemList[itemCode].matchPattern[1];
        } else if (searchItemsState[itemCode].inputLabel?.includes('to')) {
            searchItem.match = searchItemsState[itemCode].inputLabel?.startsWith('to') ? conditionItemList[itemCode].matchPattern[2] : searchItemsState[itemCode].inputLabel?.endsWith('to') ? conditionItemList[itemCode].matchPattern[1] : conditionItemList[itemCode].matchPattern[0];
        } else {
            searchItem.match = conditionItemList[itemCode].matchPattern[0];
        }

        return searchItem;
    }
    const handleClickSearch = async () => {
        // セッションの有効性確認
        if (await sessionDisconnectedCheck(screenId, 'handleClickSearch', navigate)) {
            return
        }
        const itemCodeList = Object.keys(conditionItemList);
        const searchItems: SearchItem[] = []
        for (let itemCode of itemCodeList) {
            if (searchItemsState[itemCode].inputValue !== '') {
                searchItems.push(createSearchItemRequest(itemCode));
            }
        }

        if (searchItems.length === 0) {
            SwalUtil.labelInfo({
                title: "検索条件未設定",
                message: '検索条件を設定してください。',
                textAlign: "center",
                confirmButtonText: "OK",
                buttonAlign: "right",
                icon: 'error',
                buttonOrder: { confirm: 2, deny: 1 }
            });
        } else {
            setLoading(true)
            const session = await fetchAuthSession();
            const email = session.tokens?.idToken?.payload?.email;
            const sessionId = session.tokens?.idToken?.payload?.sessionId;
            const accountInfoData = JSON.parse(session.tokens?.idToken?.payload?.accountInfoData?.toString() || "[]");
            const contract = JSON.parse(session?.tokens?.idToken?.payload.contractInfoData?.toString() || "[]");
            const contractForm = contract.length !== 0 && contract[0].contractForm;
            const productCode = contract.length !== 0 && contract[0].productCode;
            const request: GetKigyoCountApiRequest = {
                sessionId: sessionId ? sessionId.toString() : '',
                userMailAddress: email ? email.toString() : '',
                productCode: productCode,
                contractForm: contractForm,
                personalInfo: personalInfoProtectFlag,
                searchItems: searchItems,
                usingStartDate: contract.length !== 0 && accountInfoData[0].usingStartDate
            }

            const response = getKigyoCount(request)
            const resCd = (await response).resCd;

            if (resCd === 'I0000') {
                const conditionList = (await response).data?.conditionList;
                const dataCount = (await response).data?.dataCount;
                setSearchRequest(request);
                setResultConditionList(conditionList ? conditionList : []);
                setResultCount(dataCount ? dataCount : 0);
                setIsSearched(false);
            } else {
                navigate("/Error", { state: { title: SystemErrorTitle, message: SystemErrorMsg } });
            }
            setLoading(false)
        }
    }

    const handleClickClear = () => {
        setSearchItemsState(initialSearchItemsState);
        setResultConditionList([]);
        setResultCount(0);
    }

    // 検索結果取得API呼び出し
    const handleClickDispKigyoInfoList = async () => {
        // セッションの有効性確認
        if (await sessionDisconnectedCheck(screenId, 'handleClickDispKigyoInfoList', navigate)) {
            return
        }
        if (resultCount > 1000) {
            SwalUtil.labelInfo({
                title: "検索結果件数上限",
                message: '検索結果は最新売上の上位1,000件までしか<br/>表示されません。',
                textAlign: "left",
                confirmButtonText: "OK",
                denyButtonText: "キャンセル",
                buttonAlign: "right",
                buttonOrder: { confirm: 2, deny: 1 }
            }).then(async (result) => {
                if (result.isConfirmed) {
                    setLoading(true)
                    const request: GetSearchResultApiRequest = searchRequest!
                    const response = getSearchResult(request)
                    const resCd = (await response).resCd;
                    if (resCd === 'I0000') {
                        const searchConditions = resultConditionList;
                        navigate('/result', { state: { searchConditions, searchItemsState, resultCount } });
                    } else {
                        navigate("/Error", { state: { title: SystemErrorTitle, message: SystemErrorMsg } });
                    }
                }
            });
        } else {
            setLoading(true)
            const request: GetSearchResultApiRequest = searchRequest!
            const response = getSearchResult(request)
            const resCd = (await response).resCd;
            if (resCd === 'I0000') {
                const searchConditions = resultConditionList;
                navigate('/result', { state: { searchConditions, searchItemsState, resultCount } });
            } else {
                navigate("/Error", { state: { title: SystemErrorTitle, message: SystemErrorMsg } });
            }
        }
        setLoading(false)
    }

    return (
        <>
            <BackdropLoading open={loading} />
            <Box className="headerLabel">
                検索条件入力
            </Box>
            <Box p={3} marginTop={5}>
                <Container maxWidth='xl' disableGutters>
                    <Box>
                        <Grid container={true} justifyContent='center' gap={3}>
                            <Grid size={5.8}>
                                <ConditionalParts searchItem={searchItemsState.kigyoCode} handlFunction={kigyoCodeHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.jojo} handlFunction={jojoHandleFunction} />
                                <Box p={0.8}>
                                    <Grid container={true} sx={{ width: '100%' }}>
                                        <Grid size={10}></Grid>
                                        <Grid size={2}>
                                            <Typography sx={{ fontSize: '15px', color: '#999595ff', height: '40px', lineHeight: '40px', display: 'flex', justifyContent: 'flex-end' }}>完全一致</Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                                <ConditionalParts searchItem={searchItemsState.shogoKana} handlFunction={shogoKanaHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.shogoKanji} handlFunction={shogoKanjiHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.daihyoshaKana} handlFunction={daihyoshaKanaHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.daihyoshaKanji} handlFunction={daihyoshaKanjiHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.yubinNo} handlFunction={yubinNoHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.shozaichi} handlFunction={shozaichiHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.denwaNo} handlFunction={denwaNoHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.setsuritsu} handlFunction={setsuritsuHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.sogyo} handlFunction={sogyoHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.shihonkin} handlFunction={shihonkinHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.jugyoinsu} handlFunction={jugyoinsuHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.gyoshu} handlFunction={gyoshuHandleFunction} />
                            </Grid>
                            <Divider orientation="vertical" flexItem />
                            <Grid size={5.8}>
                                <ConditionalParts searchItem={searchItemsState.yakuin} handlFunction={yakuinHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.okabunushi} handlFunction={okabunushiHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.eigyoshumoku} handlFunction={eigyoshumokuHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.shiiresaki} handlFunction={shiiresakiHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.hanbaisaki} handlFunction={hanbaisakiHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.torihikiginko} handlFunction={torihikiginkoHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.kessangetsu} handlFunction={kessangetsuHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.saishinuriage} handlFunction={saishinuriageHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.saishinrieki} handlFunction={saishinriekiHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.uriageshinchoritsu} handlFunction={uriageshinchoritsuHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.riekishinchoritsu} handlFunction={riekishinchoritsuHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.seinengappi} handlFunction={seinengappiHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.eto} handlFunction={etoHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.shusshinko} handlFunction={shusshinkoHandleFunction} />
                                <ConditionalParts searchItem={searchItemsState.shusshinchi} handlFunction={shusshinchiHandleFunction} />
                            </Grid>
                        </Grid>
                    </Box>

                    <Box marginTop={5}>
                        <Grid container={true} sx={{ width: '50%' }} columnGap={3}>
                            <Grid size={1.8}>
                                <ItemLabelBox>
                                    <ItemLabelTypography marginLeft='10px'>検索条件</ItemLabelTypography>
                                </ItemLabelBox>
                            </Grid>
                            <Grid size={4}>
                                <WhiteButton variant='outlined' onClick={handleClickSave} sx={{ width: '100%' }}>検索条件の保存</WhiteButton>
                            </Grid>
                            <Grid size={5}>
                                <WhiteButton variant='outlined' onClick={handleClickLoadAndDelete} sx={{ width: '100%' }}>検索条件の読込・削除</WhiteButton>
                            </Grid>
                        </Grid>
                    </Box>

                    {resultConditionList.length !== 0 &&
                        <Box marginTop={1}>
                            <TableContainer sx={{ border: '1px solid', borderColor: '#01CFBE', width: '100%', paddingBottom: '10px' }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <ConditionalTableHeadCell width='10%'>選択条件</ConditionalTableHeadCell>
                                            <ConditionalTableHeadCell width='20%'>件数</ConditionalTableHeadCell>
                                            <ConditionalTableHeadCell width='70%'>入力値</ConditionalTableHeadCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {resultConditionList.map((condition) => (
                                            <TableRow>
                                                <ConditionalTableBodyCell width='10%'>{condition.itemLabel}</ConditionalTableBodyCell>
                                                <ConditionalTableBodyCell width='20%'>{condition.count.toLocaleString()}</ConditionalTableBodyCell>
                                                <ConditionalTableBodyCell sx={{ wordBreak: 'break-all' }} width='70%'>{condition.value}</ConditionalTableBodyCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    }

                    <Box marginTop={5}>
                        <Grid container={true} columnGap={1.4} sx={{ width: '100%' }}>
                            <Grid size={5}></Grid>
                            <Grid size={2}>
                                <GreenButton variant='contained' color='secondary' onClick={() => handleClickSearch()} startIcon={<SearchIcon />} sx={{ width: '100%' }}>検索</GreenButton>
                            </Grid>
                            <Grid size={1.7}>
                                <WhiteButton variant='outlined' onClick={handleClickClear} sx={{ width: '100%' }}>検索条件のクリア</WhiteButton>
                            </Grid>
                            <Grid size={1.5}>
                                <Typography variant='h5' width='100%' textAlign='center'>{resultCount.toLocaleString()}</Typography>
                            </Grid>
                            <Grid size={0.3}>
                                <Typography variant='h5' textAlign='center' width='100%'>件</Typography>
                            </Grid>
                            <Grid size={1}>
                                <GreenButton disabled={resultCount === 0 || isSearched} variant='contained' onClick={() => handleClickDispKigyoInfoList()} sx={{ width: '100%' }}>一覧表示</GreenButton>
                            </Grid>
                        </Grid>
                    </Box>
                </Container>
            </Box>
            <SaveCondition isOpen={saveOpen} searchConditon={saveParameterJson} handleClose={() => setSaveOpen(false)}></SaveCondition>
            <LoadCondition isOpen={loadAndDeleteOpen} handleClose={() => setLoadAndDeleteOpen(false)} handleLoad={handleLoad}></LoadCondition>

        </>
    );
}
export default InputSearchCondition;
